import React from 'react';
import { 
  makeStyles, 
  Grid, 
  TextField, 
  Button, 
  FormControl,
  Select,
  MenuItem,
  Paper,
  Box,
  Typography
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles((theme) => ({
  searchForm: {
    padding: '16px 20px',
    marginBottom: 37,
    border: '1px solid #DFE0EB',
    borderRadius: 2,
    backgroundColor: '#fff',
  },
  formField: {
    marginBottom: 16,
  },
  fieldLabel: {
    marginBottom: 8,
    fontWeight: 500,
    color: '#333',
    fontSize: '14px',
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4285f4',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #4285f4',
        boxShadow: '0 0 10px 3px rgba(66, 133, 244, 0.1)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d5d5d5',
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: '10px',
      paddingBottom: '10px',
    },
  },
  buttonGroup: {
    marginTop: 8,
    display: 'flex',
    gap: 12,
  },
  searchButton: {
    minWidth: 120,
    borderRadius: 2,
    textTransform: 'uppercase',
    fontWeight: 500,
  },
  clearButton: {
    minWidth: 100,
    borderRadius: 2,
    textTransform: 'uppercase',
    fontWeight: 500,
  },
}));

const GenericSearchForm = ({
  searchParams,
  onSearchParamsChange,
  onSearch,
  onClear,
  isLoading = false,
  fields = [], // Array of field configurations
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const handleFieldChange = (field) => (event) => {
    onSearchParamsChange({
      ...searchParams,
      [field]: event.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSearch();
  };

  const handleClear = () => {
    // Create empty object with all field keys
    const emptyParams = {};
    fields.forEach(field => {
      emptyParams[field.key] = '';
    });
    onSearchParamsChange(emptyParams);
    onClear();
  };

  const renderField = (field) => {
    const { key, label, placeholder, type, options, gridProps } = field;

    if (type === 'select') {
      return (
        <FormControl 
          fullWidth 
          variant="outlined" 
          size="small"
          className={classes.inputField}
        >
          <Select
            value={searchParams[key] || ''}
            onChange={handleFieldChange(key)}
            displayEmpty
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={searchParams[key] || ''}
        onChange={handleFieldChange(key)}
        className={classes.inputField}
        placeholder={placeholder}
      />
    );
  };

  return (
    <Paper className={classes.searchForm} elevation={0}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {fields.map((field) => (
            <Grid item key={field.key} {...field.gridProps}>
              <Box className={classes.formField}>
                <Typography className={classes.fieldLabel}>
                  {field.label}
                </Typography>
                {renderField(field)}
              </Box>
            </Grid>
          ))}
        </Grid>
        
        <Box className={classes.buttonGroup}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            className={classes.searchButton}
            disabled={isLoading}
          >
            Search
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleClear}
            disabled={isLoading}
            className={classes.clearButton}
          >
            Clear
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default GenericSearchForm;
